import { FC, useEffect, useState } from 'react';

import { ActionConfirmation, Box } from '@qwealth/qcore';
import { QAttachments } from '@qwealth/qdata';
import { FileIcon } from '@qwealth/qvault';

import { downloadAttachment, deleteAttachment } from 'data/data-layer/attachments';
import { StyledAttachmentCard } from './AttachmentList.styles';

// TODO (QCON-373): remove once qdata is rebuilt and QAttachmentFile is available on QAttachments
type QAttachmentsWithFile = QAttachments & { file: { id: number; name: string } };


interface Props {
  attachments: QAttachments[];
  label?: string;
  householdQID?: string;
  isFileDeletable?: boolean;
  onQueueDelete?: (id: string) => void;
}

const AttachmentList: FC<Props> = ({ attachments, label, householdQID, isFileDeletable = false, onQueueDelete }) => {
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setDeletedIds(new Set());
  }, [attachments]);

  const visible = (attachments as QAttachmentsWithFile[]).filter(a => !deletedIds.has(String(a.id)) && a.file != null);

  const getFileExtension = (name: string) => name.split('.').at(-1) ?? '';

  const handleDownload = async (attachment: QAttachmentsWithFile) => {
    try {
      const blob = await downloadAttachment(attachment.file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      console.error('Attachment download failed', e);
    }
  };

  const handleDelete = async (attachment: QAttachmentsWithFile) => {
    const markDeleted = () => setDeletedIds(prev => {
      const next = new Set(prev);
      next.add(String(attachment.id));
      return next;
    });

    if (onQueueDelete) {
      markDeleted();
      onQueueDelete(String(attachment.id));
      return;
    }
    try {
      await deleteAttachment(attachment.id, householdQID!);
      markDeleted();
    } catch (e) {
      console.error('Attachment delete failed', e);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap="default" width="100%">
      {label && <Box fontWeight="bold" mb="small">{label}</Box>}
      {visible.length === 0 ? (
        <Box as="span" fontStyle="italic">You don't have any attachments.</Box>
      ) : visible.map(attachment => (
        <StyledAttachmentCard key={attachment.id}>
          <Box
            className="file-icon-link-wrapper"
            role="button"
            tabIndex={0}
            onClick={() => handleDownload(attachment)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleDownload(attachment);
              }
            }}
          >
            <FileIcon fileExtension={getFileExtension(attachment.file.name)} style={{ fontSize: '24px', flexShrink: 0 }} />
            <Box className="link">{attachment.file.name}</Box>
          </Box>
          <Box className="delete-button">
            {isFileDeletable && householdQID && (
              <ActionConfirmation
                actionHandler={() => handleDelete(attachment)}
                target={`delete ${attachment.file.name}`}
              />
            )}
          </Box>
        </StyledAttachmentCard>
      ))}
    </Box>
  );
};

export default AttachmentList;
